"use client";
import { createContext, ReactNode, useContext, useRef, useState } from "react";
import { useEffectOnce } from "react-use";
import { BehaviorSubject } from "rxjs";

export interface SectionNav {
  navId: string;
  href: string;
}

export interface SectionNavContext {
  sections: SectionNav[];
  observer: BehaviorSubject<SectionNav["navId"]>;
}

const scrollSpyContext = createContext<SectionNavContext>({
  sections: [],
  observer: new BehaviorSubject(""),
});
export const ScrollSpyProvider = scrollSpyContext.Provider;
export const useScrollSpy = () => {
  const context = useContext(scrollSpyContext);
  if (!context) {
    throw new Error(
      "useScrollSpy must be used within a ScrollSpy or ScrollSpyProvider",
    );
  }
  return context;
};

interface ScrollSpyProps {
  children: ReactNode;
}

const ScrollSpy = (props: ScrollSpyProps) => {
  const { children } = props;
  const [sections, setSections] = useState<SectionNavContext["sections"]>([]);
  const $activeSection = useRef(
    new BehaviorSubject<SectionNav["navId"]>(""),
  ).current;

  useEffectOnce(() => {
    if (!document) return;
    const sections = document.querySelectorAll("[nav-id]");
    let temp: SectionNav[] = [];
    sections.forEach((section) => {
      const navId = section.getAttribute("nav-id");
      if (!navId) return;
      temp.push({
        navId,
        href: `#${navId}`,
      });
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target?.getAttribute("nav-id");
          if (entry.isIntersecting && id) {
            $activeSection.next(id);
          }
        });
      },
      {
        rootMargin: "-200px 0px -70% 0px ",
      },
    );

    sections.forEach((section) => {
      observer.observe(section);
    });
    setSections(temp);
    if (temp.length > 0) {
      $activeSection.next(temp[0].navId);
    }
  });

  return (
    <ScrollSpyProvider
      value={{
        sections,
        observer: $activeSection,
      }}
    >
      {children}
    </ScrollSpyProvider>
  );
};
export default ScrollSpy;
